/** @jsxImportSource @emotion/react */
import * as R from 'ramda';
import * as React from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { connect } from 'react-redux';
import solidArrow from '../../assets/icons/downArrowSolid.svg';
import { ResortToolsConfig } from '../../auth/config/resortToolsConfig';
import { IKON_MODAL } from '../../core/modal/ikonModal';
import { showModalAction } from '../../core/modal/showModalAction';
import { IAppState } from '../../core/redux/IAppState';
import { importGuestDisabledMessage, passPrintDisabledMessage } from '../../guest/featureDisabling';
import { GuestDetails } from '../../guest/guestDetails';
import { GuestInfo } from '../../guest/guestInfo';
import { importGuestAction } from '../../guest/importGuest/importGuestAction';
import { not } from '../../language/module';
import FloatingMenu from '../../layout/menu/FloatingMenu';
import { MenuEntry } from '../../layout/menu/menuEntry';
import { LAYER } from '../../layout/zIndex';
import { showSnackbarAction } from '../../snackbar/snackbarAction';
import { MediumText, UNSELECTABLE_TEXT } from '../../styles/styles';
import { getHousehold } from '../module';
import { getResource } from '../../language/resources/getResource';


const styles = {
  menuRoot: css`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    ${UNSELECTABLE_TEXT};
  `,
  menu:     css`
    width: 200px;
  
    /* When wrapping content to column form, push the menu off the left-hand screen edge */
    @media(max-width: 959px) {
      margin-left: 100px;
    }
  `,
  arrow:    css`
    /* Odd dimensions size image to the height of a lower-case letter @ 2rem */
    width: 19px; 
    height: 9.5px;
    padding: 5px 5px 0;
  `,
};


type StateProps = {
  config: ResortToolsConfig;
  userRole: string | undefined;
  members: GuestDetails[];
  guest: GuestDetails;
  resources: Record<string, any>;
  isImportDisabled: (guest: GuestDetails) => boolean;
  isPrintDisabled: (guest: GuestDetails) => boolean;
};

type DispatchProps = {
  importGuest: (guest: GuestDetails) => void;
  showSnackbar: (message: string) => void;
  showModal: (modalType: IKON_MODAL, extras?: any) => void;
};

type Props = {
  guestId: string;
  householdSize: number;
  disabled?: boolean;
  rootCss?: SerializedStyles;
};


/**
 * GroupMenu
 * A dropdown menu full of actions the user can take against a group of pass-holders.
 *
 * PureComponent: true
 * Connected to redux: true
 */
export default connect<StateProps, DispatchProps, Props, IAppState>(
  // Map state to props
  (state: IAppState, props: Props): StateProps => {
    const household = getHousehold(state, props.guestId);
  
    return {
      config:           state.resortToolsConfig,
      userRole:         state.userState.role,
      members:          household ? household.map(member => state.guests.get(member.masterGuestId)) : [],
      guest:            state.guests.get(props.guestId),
      resources:        state.locale,
      isImportDisabled: (guest: GuestDetails) => !!importGuestDisabledMessage(
        state.locale, 
        state.resortToolsConfig.dtwCategoryId,
        state.importSuccesses,
        guest,
        state.resortToolsConfig.enableCustomerImport.includes(state.userState.role ?? "")
      ),
      isPrintDisabled:  (guest) => passPrintDisabledMessage(
        state.locale,
        state.resortToolsConfig.dtwCategoryId,
        guest,
        state.entitlementTypesDetails!.allEntitlementTypes,
      ) !== undefined,
    };
  },
  // Map dispatch to props
  dispatch => ({
    importGuest:  (guest: GuestInfo) => dispatch(importGuestAction(guest)),
    showSnackbar: (message: string) => dispatch(showSnackbarAction(message)),
    showModal:    (modalType: IKON_MODAL, extras?: any) => dispatch(showModalAction(modalType, extras)),
  }),
)(class GroupMenu extends React.PureComponent<StateProps & DispatchProps & Props> {
  private isPrintDisabled = (): boolean => !R.any(member => !this.props.isPrintDisabled!(member), this.props.members!);


  private isImportDisabled = (): boolean => !R.any(not(this.props.isImportDisabled!), this.props.members!);


  private getMenuTitle = (isOpen: boolean): string => {
    if (isOpen) {
      return getResource(this.props.resources, 'GROUP_MENU_CLOSE_ACTIONS');
    }

    if (this.props.disabled) {
      return getResource(this.props.resources, 'GROUP_MENU_EXPAND_GROUP');
    }

    return getResource(this.props.resources, 'GROUP_MENU_OPEN_GROUP_ACTIONS');
  };


  private getPrintTitle = (): string => this.isPrintDisabled()
    ? getResource(this.props.resources, 'GROUP_MENU_NO_GUESTS_CAN_BE_PRINTED')
    : getResource(this.props.resources, 'GROUP_MENU_PRINT_PASSES');


  private getImportTitle = (): string => this.isImportDisabled()
    ? getResource(this.props.resources, 'GROUP_MENU_NO_GUESTS_ELIGIBLE_FOR_IMPORT')
    : getResource(this.props.resources, 'GROUP_MENU_IMPORT_ALL_HOUSEHOLD');


  private onImportClick = (): void => {
    if (!this.props.members) {
      return;
    }

    this.props.showSnackbar!(getResource(this.props.resources, 'GROUP_MENU_IMPORTING_GROUP'));

    this.props.members.filter(member => !this.props.isImportDisabled!(member)).forEach(this.props.importGuest);
  };


  private onPrintClick = (): void => this.props.showModal!(
    IKON_MODAL.GUEST_GROUP_PRINT,
    { guestId: this.props.guest!.masterGuestId },
  );


  private makeMenuItems = (config: ResortToolsConfig, role: string): MenuEntry[] => [
    {
      key:     'groupPrintButton',
      text:    getResource(this.props.resources, 'GROUP_MENU_PRINT_GROUP'),
      title:   this.getPrintTitle,
      onClick: this.onPrintClick,
      disable: this.isPrintDisabled,
    }, {
      key:     'groupImportButton',
      text:    getResource(this.props.resources, 'GROUP_MENU_IMPORT_GROUP'),
      title:   this.getImportTitle,
      onClick: this.onImportClick,
      disable: this.isImportDisabled,
    },
  ];


  public render = () => {
    return (
      <FloatingMenu
        menuZIndex={LAYER.THREE.FLOATING_CONTENT}
        menuItems={this.makeMenuItems(this.props.config!, this.props.userRole!)}
        disabled={this.props.disabled}
        menuTitle={this.getMenuTitle}
        rootCss={css`
          text-align: right;
          ${this.props.rootCss};
        `}
        menuCss={styles.menu}
        innerCss={styles.menuRoot}
        children={
          <React.Fragment>
            <span css={MediumText}>{getResource(this.props.resources, 'GROUP_MENU_GROUP', this.props.householdSize)}</span>
            <img alt='arrow' src={solidArrow} css={styles.arrow} />
          </React.Fragment>
        }
      />
    );
  };
});
